/**
 * ProjectCalendar Component
 * 
 * This component displays the project calendar with a two-panel layout:
 * - Left panel: Upcoming project events list
 * - Right panel: Full calendar view
 */

import { useState } from 'react';
import { Row, Col, Button, Form, Dropdown, Badge, Modal } from 'react-bootstrap';
import Select from 'react-select';
import { AddEventModal, EditEventModal, EventDetailsModal } from './ProjectCalendarModals';
import './ProjectCalendar.css';
import FilterProjectEventsOffcanvas from './ProjectCalendarFilter';

const ProjectCalendar = ({ projectId, projectName }) => {
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [showEditEventModal, setShowEditEventModal] = useState(false);
    const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
    const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
    const [showTodayMeetingModal, setShowTodayMeetingModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [hoveredEventId, setHoveredEventId] = useState(null);
    const [calendarView, setCalendarView] = useState('month'); // month, week, day
    const [currentDate, setCurrentDate] = useState(new Date());
    const [searchQuery, setSearchQuery] = useState('');
    const [filterEventType, setFilterEventType] = useState({ value: 'all', label: 'All Types' });

    // Event types with colors
    const eventTypes = [
        { value: 'meeting', label: 'Meeting', color: '#3b82f6', icon: 'fa-users' },
        { value: 'deadline', label: 'Deadline', color: '#ef4444', icon: 'fa-flag' },
        { value: 'milestone', label: 'Milestone', color: '#8b5cf6', icon: 'fa-trophy' },
        { value: 'task', label: 'Task Due', color: '#f97316', icon: 'fa-tasks' },
        { value: 'note', label: 'Custom Note', color: '#10b981', icon: 'fa-sticky-note' },
        { value: 'review', label: 'Review Session', color: '#06b6d4', icon: 'fa-eye' },
        { value: 'deployment', label: 'Deployment', color: '#ec4899', icon: 'fa-rocket' },
        { value: 'workshop', label: 'Workshop', color: '#f59e0b', icon: 'fa-chalkboard-teacher' }
    ];

    // Events state - CHANGED from static mockEvents to stateful events
    const [events, setEvents] = useState([
        { id: 1, title: 'Client Kickoff Meeting', type: 'meeting', startDate: '2025-12-05', startTime: '10:00', endTime: '11:30', description: 'Initial project discussion with stakeholders', assignedTo: ['John Doe', 'Sarah Wilson'], priority: 'high', location: 'Conference Room A' },
        { id: 2, title: 'Design Mockup Deadline', type: 'deadline', startDate: '2025-12-08', startTime: '18:00', endTime: '18:00', description: 'Final submission of homepage designs', assignedTo: ['Sarah Wilson'], priority: 'high' },
        { id: 3, title: 'Sprint 1 Completion', type: 'milestone', startDate: '2025-12-10', startTime: '17:00', endTime: '17:00', description: 'Complete all sprint 1 tasks', assignedTo: ['Mike Johnson', 'Emily Davis'], priority: 'medium' },
        { id: 4, title: 'API Integration Task', type: 'task', startDate: '2025-12-12', startTime: '14:00', endTime: '16:00', description: 'Complete payment gateway integration', assignedTo: ['Mike Johnson'], priority: 'high' },
        { id: 5, title: 'Project Scope Review', type: 'review', startDate: '2025-12-15', startTime: '15:00', endTime: '16:30', description: 'Review and adjust project scope', assignedTo: ['John Doe', 'David Brown'], priority: 'medium', location: 'Virtual Meeting' },
        { id: 6, title: 'Team Workshop', type: 'workshop', startDate: '2025-12-18', startTime: '09:00', endTime: '12:00', description: 'Agile methodology training', assignedTo: ['All Team'], priority: 'low', location: 'Training Room' },
        { id: 7, title: 'Beta Deployment', type: 'deployment', startDate: '2025-12-20', startTime: '00:00', endTime: '02:00', description: 'Deploy to staging environment', assignedTo: ['Mike Johnson'], priority: 'high' },
        { id: 8, title: 'Remember: Client Birthday', type: 'note', startDate: '2025-12-22', startTime: '09:00', endTime: '09:00', description: 'Send greeting card', assignedTo: ['Sarah Wilson'], priority: 'low' },
        { id: 9, title: 'Final Review Meeting', type: 'meeting', startDate: '2025-12-25', startTime: '11:00', endTime: '13:00', description: 'Final walkthrough before launch', assignedTo: ['John Doe', 'Sarah Wilson', 'Mike Johnson'], priority: 'high', location: 'Main Office' },
        { id: 10, title: 'Project Launch', type: 'milestone', startDate: '2025-12-28', startTime: '10:00', endTime: '10:00', description: 'Official project go-live', assignedTo: ['All Team'], priority: 'high' }
    ]);

    const teamMembers = [
        { value: 'john', label: 'John Doe' },
        { value: 'sarah', label: 'Sarah Wilson' },
        { value: 'mike', label: 'Mike Johnson' },
        { value: 'emily', label: 'Emily Davis' },
        { value: 'david', label: 'David Brown' }
    ];

    // Event CRUD handlers
    const handleAddEvent = (newEvent) => {
        const eventWithId = {
            ...newEvent,
            id: events.length > 0 ? Math.max(...events.map(e => e.id)) + 1 : 1
        };
        setEvents([...events, eventWithId]);
        setShowAddEventModal(false);
    };

    const handleEditEvent = (updatedEvent) => {
        setEvents(events.map(event => 
            event.id === updatedEvent.id ? updatedEvent : event
        ));
        setShowEditEventModal(false);
        setSelectedEvent(null);
    };

    const handleDeleteEvent = (eventId) => {
        if (window.confirm('Are you sure you want to delete this event?')) {
            setEvents(events.filter(event => event.id !== eventId));
            setShowEventDetailsModal(false);
            setSelectedEvent(null);
        }
    };

    // Filter events
    const getFilteredEvents = () => {
        return events.filter(event => {
            const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.description.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesType = filterEventType.value === 'all' || event.type === filterEventType.value;
            return matchesSearch && matchesType;
        });
    };

    const filteredEvents = getFilteredEvents();

    // Get upcoming events (sorted by date)
    const upcomingEvents = [...filteredEvents].sort((a, b) => 
        new Date(a.startDate) - new Date(b.startDate)
    );

    const getEventTypeConfig = (type) => {
        return eventTypes.find(t => t.value === type) || eventTypes[0];
    };

    const handleEventAction = (action, event) => {
        setSelectedEvent(event);
        if (action === 'edit') {
            setShowEditEventModal(true);
        } else if (action === 'view') {
            setShowEventDetailsModal(true);
        } else if (action === 'delete') {
            handleDeleteEvent(event.id);
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (time) => {
        if (!time || time === '00:00') return '';
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    // Generate calendar days for current month
    const generateCalendarDays = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        
        const days = [];
        
        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }
        
        // Add all days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        
        return days;
    };

    const getEventsForDate = (day) => {
        if (!day) return [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return filteredEvents.filter(event => event.startDate === dateStr);
    };

    const isToday = (day) => {
        const today = new Date();
        return day === today.getDate() && 
               currentDate.getMonth() === today.getMonth() && 
               currentDate.getFullYear() === today.getFullYear();
    };

    const changeMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Get today's meetings
    const getTodayMeetings = () => {
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        return events.filter(event => event.startDate === todayStr && event.type === 'meeting');
    };

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];

    return (
        <>
            <Row className="g-4">
                {/* LEFT PANEL - Clean Minimal Design */}
                <Col lg={4} xl={3}>
                    <div className="calendar-sidebar-card">
                        {/* Simple Header */}
                        <div className="calendar-sidebar-header">
                            <div className="d-flex align-items-center justify-content-between">
                                <h6 className="mb-0 fw-semibold">Events</h6>
                                <Badge bg="light" text="dark" className="calendar-count-badge-new">
                                    {upcomingEvents.length}
                                </Badge>
                            </div>
                        </div>

                        {/* Compact Search Bar */}
                        <div className="calendar-search-section">
                            <div className="calendar-search-wrapper">
                                <i className="fas fa-search"></i>
                                <Form.Control
                                    type="text"
                                    placeholder="Search events..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="calendar-search-input"
                                />
                            </div>
                            
                            <div className="calendar-filter-row">
                                <Select
                                    value={filterEventType}
                                    onChange={(selectedOption) => setFilterEventType(selectedOption)}
                                    options={[
                                        { value: 'all', label: 'All Types' },
                                        ...eventTypes.map(type => ({ value: type.value, label: type.label }))
                                    ]}
                                    className="flex-grow-1"
                                    classNamePrefix="react-select"
                                    placeholder="Select type..."
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '38px',
                                            fontSize: '13px',
                                            borderColor: '#e2e8f0',
                                            '&:hover': { borderColor: '#cbd5e0' }
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            fontSize: '13px'
                                        })
                                    }}
                                />
                                <Button 
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setShowFilterOffcanvas(true)}
                                    title="More Filters"
                                    className="calendar-filter-btn-new"
                                >
                                    <i className="fas fa-sliders-h"></i>
                                </Button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="calendar-add-section">
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="outline-primary"
                                    size="sm"
                                    className="flex-fill"
                                    onClick={() => setShowAddEventModal(true)}
                                >
                                    <i className="fas fa-plus me-1"></i>
                                    Add Event
                                </Button>
                                <Button 
                                    variant="primary"
                                    size="sm"
                                    className="flex-fill"
                                    onClick={() => setShowTodayMeetingModal(true)}
                                >
                                    <i className="fas fa-video me-1"></i>
                                    Today's Meeting
                                </Button>
                            </div>
                        </div>

                        {/* Clean Events List */}
                        <div className="calendar-events-list">
                            {upcomingEvents.length > 0 ? (
                                upcomingEvents.map((event, index) => {
                                    const typeConfig = getEventTypeConfig(event.type);
                                    return (
                                        <div 
                                            key={event.id} 
                                            className={`calendar-event-card ${hoveredEventId === event.id ? 'event-hovered' : ''}`}
                                            onClick={() => handleEventAction('view', event)}
                                            onMouseEnter={() => setHoveredEventId(event.id)}
                                            onMouseLeave={() => setHoveredEventId(null)}
                                        >
                                            {/* Color Indicator */}
                                            <div 
                                                className="event-color-bar"
                                                style={{ background: typeConfig.color }}
                                            />
                                            
                                            <div className="event-card-content">
                                                {/* Icon Circle */}
                                                <div 
                                                    className="event-icon-circle"
                                                    style={{ 
                                                        background: `${typeConfig.color}12`,
                                                        color: typeConfig.color 
                                                    }}
                                                >
                                                    <i className={`fas ${typeConfig.icon}`}></i>
                                                </div>

                                                {/* Event Info */}
                                                <div className="event-info">
                                                    <div className="event-header">
                                                        <h6 className="event-title">{event.title}</h6>
                                                        <Dropdown align="end" onClick={(e) => e.stopPropagation()} className="event-dropdown-wrapper">
                                                            <Dropdown.Toggle className="event-menu-toggle">
                                                                <i className="fas fa-ellipsis-h"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="event-dropdown-menu">
                                                                <Dropdown.Item 
                                                                    onClick={(e) => { e.stopPropagation(); handleEventAction('view', event); }}
                                                                    className="dropdown-item-custom"
                                                                >
                                                                    <i className="far fa-eye"></i>
                                                                    <span>View Details</span>
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={(e) => { e.stopPropagation(); handleEventAction('edit', event); }}
                                                                    className="dropdown-item-custom"
                                                                >
                                                                    <i className="far fa-edit"></i>
                                                                    <span>Edit Event</span>
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item 
                                                                    onClick={(e) => { e.stopPropagation(); handleEventAction('delete', event); }}
                                                                    className="dropdown-item-custom dropdown-item-danger"
                                                                >
                                                                    <i className="far fa-trash-alt"></i>
                                                                    <span>Delete</span>
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>

                                                    {/* Type Badge */}
                                                    <span 
                                                        className="event-type-badge"
                                                        style={{ 
                                                            background: `${typeConfig.color}18`,
                                                            color: typeConfig.color 
                                                        }}
                                                    >
                                                        {typeConfig.label}
                                                    </span>

                                                    {/* Meta Info */}
                                                    <div className="event-meta">
                                                        <div className="event-meta-item event-meta-black">
                                                            <i className="far fa-calendar-alt"></i>
                                                            <span>{formatDate(event.startDate)}</span>
                                                        </div>
                                                        
                                                        {formatTime(event.startTime) && (
                                                            <div className="event-meta-item event-meta-black">
                                                                <i className="far fa-clock"></i>
                                                                <span>
                                                                    {formatTime(event.startTime)}
                                                                    {event.endTime && event.endTime !== event.startTime && 
                                                                        ` - ${formatTime(event.endTime)}`
                                                                    }
                                                                </span>
                                                            </div>
                                                        )}

                                                        {event.location && (
                                                            <div className="event-meta-item event-meta-black">
                                                                <i className="fas fa-map-marker-alt"></i>
                                                                <span>{event.location}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="calendar-empty-state">
                                    <div className="empty-icon-wrapper">
                                        <i className="far fa-calendar-times"></i>
                                    </div>
                                    <h6>No Events Found</h6>
                                    <p>Try adjusting your search or filters</p>
                                    <button 
                                        className="empty-add-btn"
                                        onClick={() => setShowAddEventModal(true)}
                                    >
                                        <i className="fas fa-plus"></i>
                                        Create Event
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </Col>

                {/* RIGHT PANEL - Calendar */}
                <Col lg={8} xl={9}>
                    <div className="c-card-1">
                        <div className="c-card-header-1">
                            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
                                <h5 className="mb-0 fw-bold">
                                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                                </h5>
                                <div className="d-flex gap-2">
                                    <Button variant="outline-secondary" size="sm" onClick={() => changeMonth(-1)}>
                                        <i className="fas fa-chevron-left"></i>
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={goToToday}>
                                        Today
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={() => changeMonth(1)}>
                                        <i className="fas fa-chevron-right"></i>
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="c-card-body-1 p-0">
                            {/* Calendar Grid */}
                            <div className="calendar-container">
                                {/* Day Headers */}
                                <div className="calendar-header">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                        <div key={day} className="calendar-day-header">
                                            {day}
                                        </div>
                                    ))}
                                </div>
                                
                                {/* Calendar Days */}
                                <div className="calendar-grid">
                                    {generateCalendarDays().map((day, index) => {
                                        const dayEvents = day ? getEventsForDate(day) : [];
                                        const isTodayDay = day && isToday(day);
                                        
                                        return (
                                            <div 
                                                key={index} 
                                                className={`calendar-day ${!day ? 'empty' : ''} ${isTodayDay ? 'today' : ''}`}
                                                onClick={() => day && setShowAddEventModal(true)}
                                            >
                                                {day && (
                                                    <>
                                                        <div className="day-number">{day}</div>
                                                        <div className="day-events">
                                                            {dayEvents.map((event, idx) => {
                                                                const typeConfig = getEventTypeConfig(event.type);
                                                                const timeStr = formatTime(event.startTime);
                                                                return (
                                                                    <div 
                                                                        key={event.id}
                                                                        className={`calendar-event ${hoveredEventId === event.id ? 'event-highlighted' : ''}`}
                                                                        style={{ backgroundColor: typeConfig.color }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEventAction('view', event);
                                                                        }}
                                                                        onMouseEnter={() => setHoveredEventId(event.id)}
                                                                        onMouseLeave={() => setHoveredEventId(null)}
                                                                        title={`${timeStr ? timeStr + ' - ' : ''}${event.title}`}
                                                                    >
                                                                        <span className="event-title">{event.title}</span>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Modals with handlers */}
            <AddEventModal
                show={showAddEventModal}
                onHide={() => setShowAddEventModal(false)}
                onSave={handleAddEvent}
                eventTypes={eventTypes}
                teamMembers={teamMembers}
            />

            <EditEventModal
                show={showEditEventModal}
                onHide={() => {
                    setShowEditEventModal(false);
                    setSelectedEvent(null);
                }}
                onSave={handleEditEvent}
                event={selectedEvent}
                eventTypes={eventTypes}
                teamMembers={teamMembers}
            />

            <EventDetailsModal
                show={showEventDetailsModal}
                onHide={() => {
                    setShowEventDetailsModal(false);
                    setSelectedEvent(null);
                }}
                onEdit={() => {
                    setShowEventDetailsModal(false);
                    setShowEditEventModal(true);
                }}
                onDelete={() => handleDeleteEvent(selectedEvent?.id)}
                event={selectedEvent}
                getEventTypeConfig={getEventTypeConfig}
                formatDate={formatDate}
                formatTime={formatTime}
            />

            <FilterProjectEventsOffcanvas
                show={showFilterOffcanvas}
                onHide={() => setShowFilterOffcanvas(false)}
                eventTypes={eventTypes}
                teamMembers={teamMembers}
            />

            {/* Today's Meeting Modal */}
            <TodayMeetingModal
                show={showTodayMeetingModal}
                onHide={() => setShowTodayMeetingModal(false)}
                meetings={getTodayMeetings()}
                getEventTypeConfig={getEventTypeConfig}
                formatTime={formatTime}
                onViewEvent={(event) => {
                    setSelectedEvent(event);
                    setShowTodayMeetingModal(false);
                    setShowEventDetailsModal(true);
                }}
            />
        </>
    );
};

// Today's Meeting Modal Component
const TodayMeetingModal = ({ show, onHide, meetings, getEventTypeConfig, formatTime, onViewEvent }) => {
    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title className="display-1">
                    <i className="fas fa-video me-2 text-primary"></i>
                    Today's Meetings
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {meetings.length > 0 ? (
                    <div className="today-meetings-list">
                        {meetings.map((meeting) => {
                            const typeConfig = getEventTypeConfig(meeting.type);
                            return (
                                <div 
                                    key={meeting.id} 
                                    className="today-meeting-item"
                                    onClick={() => onViewEvent(meeting)}
                                >
                                    <div 
                                        className="meeting-icon"
                                        style={{ 
                                            background: `${typeConfig.color}15`,
                                            color: typeConfig.color 
                                        }}
                                    >
                                        <i className={`fas ${typeConfig.icon}`}></i>
                                    </div>
                                    <div className="meeting-details">
                                        <h6 className="meeting-title">{meeting.title}</h6>
                                        <div className="meeting-meta">
                                            <span className="meeting-time">
                                                <i className="far fa-clock me-1"></i>
                                                {formatTime(meeting.startTime)}
                                                {meeting.endTime && meeting.endTime !== meeting.startTime && 
                                                    ` - ${formatTime(meeting.endTime)}`
                                                }
                                            </span>
                                            {meeting.location && (
                                                <span className="meeting-location">
                                                    <i className="fas fa-map-marker-alt me-1"></i>
                                                    {meeting.location}
                                                </span>
                                            )}
                                        </div>
                                        {meeting.description && (
                                            <p className="meeting-description">{meeting.description}</p>
                                        )}
                                    </div>
                                    <i className="fas fa-chevron-right meeting-arrow"></i>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="empty-icon-wrapper mb-3">
                            <i className="fas fa-calendar-check" style={{ fontSize: '48px', color: '#cbd5e0' }}></i>
                        </div>
                        <h6 className="text-muted">No Meetings Today</h6>
                        <p className="text-muted fs-13">You don't have any meetings scheduled for today.</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ProjectCalendar;
